import { acceptHMRUpdate, defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import type { Product, ProductOrigin, ProductStatus } from '@klingerator/shared';
import { productService } from '@/services/productService';

export type ProductSortField = 'updated_at' | 'created_at' | 'name' | 'status' | 'proposed_product_code';

export interface ProductFilters {
  scope: 'mine' | 'all';
  status: ProductStatus | '';
  origin: ProductOrigin | '';
  q: string;
  sort: ProductSortField;
  order: 'asc' | 'desc';
  /** true = alleen gearchiveerde (verwijderde) producten tonen */
  archived: boolean;
  /** true = ook geneste sub-producten tonen (standaard alleen root-producten + producten zonder ouder) */
  nested: boolean;
}

const PAGE_SIZE = 50;

/** Productenlijst met filters, sortering en infinite scroll (offset-paginatie). */
export const useProductsStore = defineStore('products', () => {
  const items = ref<Product[]>([]);
  const total = ref(0);
  const loading = ref(false);
  const loadingMore = ref(false);
  const error = ref<string | null>(null);
  const filters = reactive<ProductFilters>({
    scope: 'mine',
    status: '',
    origin: '',
    q: '',
    sort: 'updated_at',
    order: 'desc',
    archived: false,
    nested: false,
  });

  /** Cache van individuele producten (naam-lookup voor breadcrumbs e.d.) */
  const byId = reactive(new Map<number, Product>());

  const hasMore = computed(() => items.value.length < total.value);

  /** Statustellers over de geladen items (niet over de hele set — dat vergt een aparte endpoint). */
  const statusCounts = computed(() => {
    const counts: Partial<Record<ProductStatus, number>> = {};
    for (const p of items.value) counts[p.status] = (counts[p.status] ?? 0) + 1;
    return counts;
  });

  let requestSeq = 0;

  function buildParams(offset: number) {
    return {
      scope: filters.scope,
      status: filters.status || undefined,
      origin: filters.origin || undefined,
      q: filters.q.trim() || undefined,
      sort: filters.sort,
      order: filters.order,
      archived: filters.archived || undefined,
      include_nested: filters.nested || undefined,
      limit: PAGE_SIZE,
      offset,
    };
  }

  /** Eerste pagina laden (vervangt de lijst). */
  async function fetch(): Promise<void> {
    const seq = ++requestSeq;
    loading.value = true;
    error.value = null;
    try {
      const page = await productService.list(buildParams(0), { silent: true });
      if (seq !== requestSeq) return; // verouderd antwoord
      items.value = page.items;
      total.value = page.total;
      for (const p of page.items) byId.set(p.id, p);
    } catch (e) {
      if (seq === requestSeq) error.value = e instanceof Error ? e.message : 'Laden mislukt';
    } finally {
      if (seq === requestSeq) loading.value = false;
    }
  }

  /** Volgende pagina toevoegen (infinite scroll). */
  async function fetchMore(): Promise<void> {
    if (loading.value || loadingMore.value || !hasMore.value) return;
    const seq = requestSeq;
    loadingMore.value = true;
    try {
      const page = await productService.list(buildParams(items.value.length), { silent: true });
      if (seq !== requestSeq) return;
      items.value.push(...page.items);
      total.value = page.total;
      for (const p of page.items) byId.set(p.id, p);
    } finally {
      loadingMore.value = false;
    }
  }

  function setSort(field: ProductSortField) {
    if (filters.sort === field) filters.order = filters.order === 'asc' ? 'desc' : 'asc';
    else {
      filters.sort = field;
      filters.order =
        field === 'name' || field === 'proposed_product_code' || field === 'status' ? 'asc' : 'desc';
    }
    return fetch();
  }

  function reset() {
    requestSeq++;
    items.value = [];
    total.value = 0;
    error.value = null;
    filters.status = '';
    filters.origin = '';
    filters.q = '';
    filters.sort = 'updated_at';
    filters.order = 'desc';
    filters.nested = false;
  }

  /** Product ophalen voor naam-lookups; cachet in `byId`. */
  async function ensure(id: number): Promise<Product | null> {
    const cached = byId.get(id);
    if (cached) return cached;
    try {
      const p = await productService.get(id, { silent: true, noToast: true });
      byId.set(id, p);
      return p;
    } catch {
      return null;
    }
  }

  function remember(p: Product) {
    byId.set(p.id, p);
    const i = items.value.findIndex((x) => x.id === p.id);
    if (i !== -1) items.value[i] = { ...items.value[i], ...p };
  }

  function forget(id: number) {
    byId.delete(id);
    const i = items.value.findIndex((x) => x.id === id);
    if (i !== -1) {
      items.value.splice(i, 1);
      total.value = Math.max(0, total.value - 1);
    }
  }

  /** Eén rij in de lijst verversen vanaf de server (alleen als het product in de lijst staat). */
  async function refreshItem(id: number): Promise<void> {
    if (!items.value.some((x) => x.id === id)) return;
    try {
      remember(await productService.get(id, { silent: true, noToast: true }));
    } catch {
      /* rij blijft zoals hij was; bij de volgende fetch klopt hij weer */
    }
  }

  /**
   * De samenstelling van de lijst is server-side veranderd (nieuw/gearchiveerd/teruggezet/plaatsing):
   * de lijstweergave herlaadt zodra die zichtbaar is (watcher in ProductListView).
   */
  const invalidatedAt = ref(0);
  function invalidate() {
    invalidatedAt.value++;
  }

  return {
    items,
    total,
    loading,
    loadingMore,
    error,
    filters,
    byId,
    hasMore,
    statusCounts,
    fetch,
    fetchMore,
    setSort,
    reset,
    ensure,
    remember,
    forget,
    refreshItem,
    invalidatedAt,
    invalidate,
  };
});

// Hot reload van de store-definitie zelf (zonder dit blijft in de browser de oude store-instantie
// draaien na een wijziging in dit bestand, tot een volledige pagina-herlaad).
if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(useProductsStore, import.meta.hot));
